import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react'
import NewWindow from 'react-new-window'
import { Language } from '@/api/Clients/Types/Language'
import {
  ChangeNotificationDto,
  isPromotionalNotificationType,
  NotificationDto,
  NotificationImage,
} from '@/api/Clients/Types/NotificationDto'
import { NameDto } from '@/api/Common/Types'
import { TickmillCompanyDto } from '@/api/Identity/Types/Company'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { Box } from '@/components/Box'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormFieldLayout,
  FormMultiSelectField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAppSelector } from '@/store'
import { AuthSessionContext } from '@/utils/AuthContext'
import { hasPermission } from '@/utils/permissionsValidator'
import { GridColumn as Column, Grid } from '@progress/kendo-react-grid'
import { LabeledValue } from 'antd/es/select'
import { CountryDto } from 'api/Identity/Types/Country'
import MoreButton from 'components/MoreButton/MoreButton'
import copy from 'copy-to-clipboard'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { ChevronDown, Copy, CopyPlus, Info, ScrollText } from 'lucide-react'
import { FormSubmitValues, InvalidCharsPattern } from 'utils/formValidation'

import AddButton from '../../../components/AddButton/AddButton'
import { NotificationBoxWrapper } from './Notification.styles'
import { NotificationImageUploadModal } from './NotificationImageUploadModal'
import {
  NotificationProductStatusField,
  NotificationProductValue,
} from './NotificationProductStatusField'

export interface AudienceTypeDto {
  id: string
  name: string
  hasValue: boolean
  restrictions?: { id: number; name: string }[]
}

export interface AudienceTypeValue {
  id: number
  name: string
  hasValue?: boolean
  value?: string
}

interface PreviewImages {
  imageUrl: string | null
  additionalImageUrl: string | null
}

interface UpdateNotificationFormValues {
  id?: string
  text: string
  name: string
  title: string
  shortText: string
  type: NameDto | null
  isEnabled: boolean
  ctaUrl: string
  ctaText: string
  startDateTime: string
  endDateTime: string
  tickmillCompanies: LabeledValue[]
  baseUrlTypeId: number | null
  countryIds: string[] | null
  languageIds: string[] | null
  tickmillProducts: NotificationProductValue[]
  excludeTickmillProducts: LabeledValue[]
  possibleTickmillProducts: LabeledValue[]
  isSilent: boolean
  isPinned: boolean
  isClosable: boolean
  // ctaUrlType: number
  notificationAudienceTypes: AudienceTypeValue[]
  platformIds: NameDto[] | null
}

interface AddImageModalProps {
  image?: NotificationImage
  visible: boolean
}

const NotificationDetailsFormUI: React.FC<
  FormikProps<UpdateNotificationFormValues> & OuterProps
> = (props) => {
  const { values, setFieldValue } = props
  const { languages, notificationPlatforms } = props
  const tickmillProducts = useAppSelector((state) => state.clients.tickmillProducts)

  const [addImageModal, setAddImageModal] = useState<AddImageModalProps>({ visible: false })
  const [isEdit, setEdit] = useState<boolean>(!props.notification)
  const [preview, setPreview] = useState<PreviewImages | null>(null)

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const getLanguageName = (languageId: string): string => {
    return languages.find((x) => x.id === languageId)?.name || ''
  }

  const isPromotionalType = isPromotionalNotificationType(values?.type)

  useEffect(() => {
    if (!isPromotionalType && (values.ctaText || values.ctaUrl || values.baseUrlTypeId != null)) {
      setFieldValue('ctaText', '')
      setFieldValue('ctaUrl', '')
      setFieldValue('baseUrlTypeId', null)
    }
  }, [isPromotionalType, setFieldValue, values.baseUrlTypeId, values.ctaText, values.ctaUrl])

  const notificationTypeOptions = useMemo(
    () => (props.notificationTypes || []).map((type) => ({ id: type.id, name: type.name })),
    [props.notificationTypes]
  )
  const baseUrlTypeOptions = useMemo(
    () => (props.baseUrlTypes || []).map((type) => ({ id: type.id, name: type.name })),
    [props.baseUrlTypes]
  )
  const platformOptions = useMemo(
    () => notificationPlatforms.map((platform) => ({ id: platform.id, name: platform.name })),
    [notificationPlatforms]
  )
  const companyOptions = useMemo(
    () => props.tickmillCompanies.map((company) => ({ id: company.id, name: company.name })),
    [props.tickmillCompanies]
  )
  const productOptions = useMemo(
    () => tickmillProducts.map((product) => ({ id: product.id, name: product.name })),
    [tickmillProducts]
  )
  const audienceOptions = useMemo(() => {
    return props.notificationAudienceTypes.map((audience) => ({
      id: audience.id,
      name: audience.name,
    }))
  }, [props.notificationAudienceTypes])
  const languageOptions = useMemo(
    () => languages.map((language) => ({ id: language.id, name: language.name })),
    [languages]
  )
  const countryOptions = useMemo(
    () => props.countries.map((country) => ({ id: country.id, name: country.name })),
    [props.countries]
  )

  return (
    <Fragment>
      {addImageModal && props.notification && (
        <NotificationImageUploadModal
          visible={addImageModal.visible}
          notificationId={props.notification.id}
          image={addImageModal.image}
          languages={languages}
          onForceRefresh={props.onForceRefresh}
          onClose={() => setAddImageModal({ image: undefined, visible: false })}
        />
      )}
      {preview && (
        <NewWindow
          onUnload={() => setPreview(null)}
          copyStyles
          features={{ left: 200, top: 200, width: 800, height: 800 }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {preview.imageUrl && (
              <img
                src={preview.imageUrl}
                alt='Main Image'
                style={{ maxWidth: '100%', marginBottom: '20px' }}
              />
            )}
            {preview.additionalImageUrl && (
              <img
                src={preview.additionalImageUrl}
                alt='Additional Image'
                style={{ maxWidth: '100%' }}
              />
            )}
          </div>
        </NewWindow>
      )}
      <div className='px-4 md:px-6'>
        <Form>
          <DataGrid
            rightAddon={<NotificationDetailsHeader {...props} isEdit={isEdit} setEdit={setEdit} />}
          >
            <DataColumn>
              <DataCard columns={1} title='Details'>
                <FormField
                  name='name'
                  label='Notification Name'
                  readOnly={!isEdit}
                  disabled={Boolean(props.notification?.id)}
                />
                <FormField name='title' label='Notification Title' readOnly={!isEdit} />
                <FormSelectField
                  name='type'
                  label='Notification Type'
                  value={values.type?.id}
                  readOnly={!isEdit}
                  options={notificationTypeOptions}
                  onChange={(value) => {
                    const notificationType = props.notificationTypes.find(
                      (type) => type.id === value
                    )
                    setFieldValue('type', notificationType || null)
                  }}
                />
                <FormField
                  name='shortText'
                  label={
                    <Box display='flex' alignItems='center' gap={2}>
                      Short Description
                      <Tooltip>
                        <TooltipTrigger>
                          <Info size={14} />
                        </TooltipTrigger>
                        <TooltipContent side='right'>
                          A short description of the text attribute up to 70 characters without any
                          formatting
                        </TooltipContent>
                      </Tooltip>
                    </Box>
                  }
                  readOnly={!isEdit}
                />
                <FormTextareaField
                  name='text'
                  label={
                    <Box display='flex' alignItems='center' gap={2}>
                      Notification Text
                      <Tooltip>
                        <TooltipTrigger>
                          <Info size={14} />
                        </TooltipTrigger>
                        <TooltipContent side='right'>
                          Display text is the text content for the notification. Can support HTML
                        </TooltipContent>
                      </Tooltip>
                    </Box>
                  }
                  readOnly={!isEdit}
                  rows={6}
                  maxLength={2000}
                />
                <FormCheckboxField
                  name='isEnabled'
                  label='Enabled'
                  value={values.isEnabled}
                  readOnly={!isEdit}
                />
                <FormDateField
                  name='startDateTime'
                  label='Start Date'
                  readOnly={!isEdit}
                  showTimeSelect
                  isClearable={false}
                  dateFormatType='dd/MM/yyyy h:mm aa'
                  value={values.startDateTime ? new Date(values.startDateTime) : null}
                />
                <FormDateField
                  name='endDateTime'
                  label='End Date'
                  readOnly={!isEdit}
                  showTimeSelect
                  isClearable={false}
                  dateFormatType='dd/MM/yyyy h:mm aa'
                  value={values.endDateTime ? new Date(values.endDateTime) : null}
                />
                {isPromotionalType && (
                  <>
                    <FormField
                      name='ctaUrl'
                      label={
                        <Box display='flex' alignItems='center' gap={2}>
                          Cta Link
                          <Tooltip>
                            <TooltipTrigger>
                              <Info size={14} />
                            </TooltipTrigger>
                            <TooltipContent side='right'>
                              Used for promo notifications. cta Link is the URL the CTA button will
                              direct to upon click.
                            </TooltipContent>
                          </Tooltip>
                        </Box>
                      }
                      readOnly={!isEdit}
                    />
                    <FormField
                      name='ctaText'
                      label={
                        <Box display='flex' alignItems='center' gap={2}>
                          Cta Text
                          <Tooltip>
                            <TooltipTrigger>
                              <Info size={14} />
                            </TooltipTrigger>
                            <TooltipContent side='right'>
                              Used for promo notifications. cta text is the text to be displayed on
                              the CTA button in CA
                            </TooltipContent>
                          </Tooltip>
                        </Box>
                      }
                      readOnly={!isEdit}
                    />
                    <FormSelectField
                      name='baseUrlTypeId'
                      label={
                        <Box display='flex' alignItems='center' gap={2}>
                          Base URL Type
                          <Tooltip>
                            <TooltipTrigger>
                              <Info size={14} />
                            </TooltipTrigger>
                            <TooltipContent side='right'>
                              baseUrlTypeId determines if the CtaUrl will direct to a specific
                              domain or leave the URL unchanged.
                            </TooltipContent>
                          </Tooltip>
                        </Box>
                      }
                      value={values.baseUrlTypeId ?? undefined}
                      readOnly={!isEdit}
                      allowClear
                      options={baseUrlTypeOptions}
                      onChange={(value) =>
                        props.setFieldValue('baseUrlTypeId', value ? Number(value) : null)
                      }
                    />
                  </>
                )}
                <FormMultiSelectField
                  name='platformIds'
                  label='Platform'
                  value={values.platformIds?.map((platform) => platform.id) || []}
                  placeholder='Select Platforms'
                  readOnly={!isEdit}
                  options={platformOptions}
                  onChange={(value) => {
                    const selectedIds = Array.isArray(value)
                      ? value.map((id) => Number(id))
                      : value != null
                        ? [Number(value)]
                        : []

                    setFieldValue(
                      'platformIds',
                      platformOptions
                        .filter((platform) => selectedIds.includes(Number(platform.id)))
                        .map((platform) => ({ id: Number(platform.id), name: platform.name }))
                    )
                  }}
                />
                <FormMultiSelectField
                  name='tickmillCompanies'
                  label='Tickmill Companies'
                  value={values.tickmillCompanies}
                  placeholder='Companies'
                  readOnly={!isEdit}
                  options={companyOptions}
                  onChange={(value) => {
                    const selectedIds = Array.isArray(value)
                      ? value.map((id) => Number(id))
                      : value != null
                        ? [Number(value)]
                        : []

                    props.setFieldValue(
                      'tickmillCompanies',
                      companyOptions
                        .filter((company) => selectedIds.includes(Number(company.id)))
                        .map((company) => ({
                          label: company.name,
                          value: company.id,
                        }))
                    )
                  }}
                />
                <NotificationProductStatusField
                  name='tickmillProducts'
                  label='Tickmill Product(s)'
                  value={values.tickmillProducts}
                  products={productOptions}
                  statuses={props.notificationProductStatuses}
                  readOnly={!isEdit}
                  onChange={(nextProducts) => setFieldValue('tickmillProducts', nextProducts)}
                />
                <FormMultiSelectField
                  name='excludeTickmillProducts'
                  label='Exclude Tickmill Product(s)'
                  value={values.excludeTickmillProducts}
                  placeholder='Excluded Product(s)'
                  readOnly={!isEdit}
                  options={productOptions}
                  onChange={(value) => {
                    const selectedIds = Array.isArray(value)
                      ? value.map((id) => Number(id))
                      : value != null
                        ? [Number(value)]
                        : []

                    props.setFieldValue(
                      'excludeTickmillProducts',
                      productOptions
                        .filter(({ id }) => selectedIds.includes(Number(id)))
                        .map(({ name, id }) => ({ label: name, value: id }))
                    )
                  }}
                />
                <FormMultiSelectField
                  name='possibleTickmillProducts'
                  label='Registrable Tickmill Product(s)'
                  value={values.possibleTickmillProducts}
                  placeholder='Tickmill Product(s)'
                  readOnly={!isEdit}
                  options={productOptions}
                  onChange={(value) => {
                    const selectedIds = Array.isArray(value)
                      ? value.map((id) => Number(id))
                      : value != null
                        ? [Number(value)]
                        : []

                    props.setFieldValue(
                      'possibleTickmillProducts',
                      productOptions
                        .filter(({ id }) => selectedIds.includes(Number(id)))
                        .map(({ name, id }) => ({ label: name, value: id }))
                    )
                  }}
                />
                <FormCheckboxField
                  name='isSilent'
                  label='Silent Notification'
                  value={values.isSilent}
                  readOnly={!isEdit}
                />
                <FormMultiSelectField
                  name='notificationAudienceTypes'
                  label={
                    <Box display='flex' alignItems='center' gap={2}>
                      Audience Types
                      <Tooltip>
                        <TooltipTrigger>
                          <Info size={14} />
                        </TooltipTrigger>
                        <TooltipContent side='right'>
                          Audience Type specifies the audience for which the notification is visible
                        </TooltipContent>
                      </Tooltip>
                    </Box>
                  }
                  value={values.notificationAudienceTypes.map((audience) => audience.id)}
                  placeholder='Select Audiences'
                  readOnly={!isEdit}
                  allowClear
                  options={audienceOptions}
                  onChange={(value) => {
                    const selectedIds = Array.isArray(value) ? value : value != null ? [value] : []

                    const updatedValues = selectedIds.map((id) => {
                      const audienceFromProps = props.notificationAudienceTypes.find(
                        (type) => type.id === id
                      )

                      return {
                        id,
                        name: audienceFromProps?.name || '',
                        hasValue: audienceFromProps?.hasValue || false,
                        restrictions: audienceFromProps?.restrictions || [],
                        value: audienceFromProps?.hasValue ? '' : undefined,
                      }
                    })

                    setFieldValue('notificationAudienceTypes', updatedValues)
                  }}
                />

                <FormMultiSelectField
                  name='languageIds'
                  label={
                    <Box display='flex' alignItems='center' gap={2}>
                      Language
                      <Tooltip>
                        <TooltipTrigger>
                          <Info size={14} />
                        </TooltipTrigger>
                        <TooltipContent side='right'>
                          Language from language picker in users Client Area (not communication
                          lang.)
                        </TooltipContent>
                      </Tooltip>
                    </Box>
                  }
                  value={values.languageIds || []}
                  placeholder='Select Languages'
                  allowClear
                  showSearch
                  readOnly={!isEdit}
                  options={languageOptions}
                  onChange={(value) =>
                    setFieldValue(
                      'languageIds',
                      Array.isArray(value) ? value.map(String) : value ? [String(value)] : null
                    )
                  }
                />
                <FormCheckboxField name='isPinned' label='Is Pinned' readOnly={!isEdit} />
                <FormCheckboxField name='isClosable' label='Is Closable' readOnly={!isEdit} />
                <FormMultiSelectField
                  name='countryIds'
                  label='Countries'
                  value={values.countryIds || []}
                  placeholder='Countries'
                  allowClear
                  showSearch
                  readOnly={!isEdit}
                  options={countryOptions}
                  onChange={(value) =>
                    setFieldValue(
                      'countryIds',
                      Array.isArray(value) ? value.map(String) : value ? [String(value)] : null
                    )
                  }
                />
              </DataCard>
            </DataColumn>
            <DataColumn>
              <DataCard title='Preview'>
                <FormFieldLayout
                  label='Notification Content'
                  rightAddon={
                    <Button
                      type='button'
                      size='sm'
                      variant='link'
                      onClick={() => copy(values.text)}
                    >
                      <Copy /> Copy to clipboard
                    </Button>
                  }
                >
                  <NotificationBoxWrapper
                    aria-disabled={!isEdit}
                    dangerouslySetInnerHTML={{ __html: values.text }}
                  />
                </FormFieldLayout>
              </DataCard>
              {props.notification && isPromotionalType && (
                <DataCard title='Promotional Images'>
                  <AddButton
                    text='Add image(s)'
                    onClick={() => setAddImageModal({ visible: true })}
                    disabled={!isEdit}
                    data-test='add-image-button'
                  />
                  <Grid data={props.notification.images}>
                    <Column
                      title='Language'
                      cell={(item) => {
                        const { languageId, isDefault } = item.dataItem as NotificationImage
                        const languageName = getLanguageName(languageId)
                        return <td>{`${languageName} ${isDefault ? '(default)' : ''}`}</td>
                      }}
                    />
                    <Column
                      width='50px'
                      cell={(item) => {
                        const image = item.dataItem as NotificationImage
                        return (
                          <td>
                            <MoreButton
                              disabled={!isEdit}
                              items={[
                                {
                                  name: 'View',
                                  'data-test': 'edit',
                                  onClick: () =>
                                    setPreview({
                                      imageUrl: image.imageUrl,
                                      additionalImageUrl: image.additionalImageUrl
                                        ? `https://cdn.tickmill.com/${image.additionalImageUrl}`
                                        : null,
                                    }),
                                },
                                {
                                  name: 'Replace',
                                  disabled: !hasPermission(
                                    permissions,
                                    PermissionsEnum.CanEditNotifications
                                  ),
                                  'data-test': 'replace',
                                  onClick: () => {
                                    setAddImageModal({
                                      image,
                                      visible: true,
                                    })
                                  },
                                },
                              ]}
                              data-test='more-button'
                            />
                          </td>
                        )
                      }}
                    />
                  </Grid>
                </DataCard>
              )}
            </DataColumn>
          </DataGrid>
        </Form>
      </div>
    </Fragment>
  )
}

interface NotificationDetailsHeaderProps
  extends FormikProps<UpdateNotificationFormValues>,
    OuterProps {
  isEdit: boolean
  setEdit(value: boolean): void
}

const NotificationDetailsHeader: React.FC<NotificationDetailsHeaderProps> = (props) => {
  const { isEdit, isSubmitting, notification } = props
  const { validateForm, setEdit, handleSubmit, resetForm } = props

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  const canEdit = hasPermission(permissions, PermissionsEnum.CanEditNotifications)

  const handleSubmitForm = () => {
    validateForm()
    if (props.isValid && !isSubmitting) {
      handleSubmit()
    }
  }

  const handleCancel = () => {
    setEdit(false)
    resetForm()
  }

  if (!notification && canEdit) {
    return (
      <ButtonGroup>
        <Button
          type='button'
          onClick={handleSubmitForm}
          data-test='save-button'
          disabled={isSubmitting}
        >
          Save
        </Button>
      </ButtonGroup>
    )
  }

  if (isEdit && canEdit) {
    return (
      <ButtonGroup>
        <Button type='button' variant='outline' onClick={handleCancel} data-test='cancel-button'>
          Cancel
        </Button>
        <Button
          type='button'
          onClick={handleSubmitForm}
          data-test='save-button'
          disabled={isSubmitting}
        >
          Save
        </Button>
      </ButtonGroup>
    )
  }

  const handleCloneNotification = () => {
    const clonedData = dataToSubmit(props.values, props.notification?.images || [])
    props.handleClone({ ...clonedData, name: props.values.name + '-CLONED', isEnabled: false })
  }

  if (canEdit && props.notification) {
    return (
      <ButtonGroup>
        <Button type='button' onClick={() => setEdit(true)} data-test='edit-button'>
          Edit
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={<Button type='button' size='icon' variant='outline' data-test='more-button' />}
          >
            <ChevronDown />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem
              key='audit'
              data-test='audit-log-menu-button'
              onSelect={() => props.setAuditLogModal(true)}
            >
              <ScrollText /> Audit Log
            </DropdownMenuItem>
            <DropdownMenuItem data-test='clone-menu-button' onSelect={handleCloneNotification}>
              <CopyPlus /> Clone
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </ButtonGroup>
    )
  }

  return null
}

interface OuterProps {
  notification?: NotificationDto
  notificationAudienceTypes: AudienceTypeDto[]
  notificationTypes: NameDto[]
  notificationProductStatuses: NameDto[]
  countries: CountryDto[]
  tickmillCompanies: TickmillCompanyDto[]
  baseUrlTypes: NameDto[]
  languages: Language[]
  notificationPlatforms: NameDto[]
  setAuditLogModal(value: boolean): void
  handleClone(v: FormSubmitValues<ChangeNotificationDto>): void
  onSubmit(id: string, values: FormSubmitValues<ChangeNotificationDto>): Promise<void>
  onForceRefresh(): void
}

function dataToSubmit(
  values: UpdateNotificationFormValues,
  images: NotificationImage[]
): FormSubmitValues<ChangeNotificationDto> {
  return {
    id: values.id,
    name: values.name,
    title: values.title,
    text: values.text,
    shortText: values.shortText || null,
    ctaUrl: values.ctaUrl || null,
    ctaText: values.ctaText || null,
    startDateTime: values.startDateTime,
    endDateTime: values.endDateTime,
    isEnabled: values.isEnabled,
    notificationTypeId: values.type ? values.type?.id : 0,
    tickmillCompanyIds: values.tickmillCompanies.map((company) => Number(company.value)),
    tickmillProducts: values.tickmillProducts.map((product) => ({
      id: product.id,
      statuses: product.statuses.map((status) => ({
        id: status.id,
        name: status.name,
      })),
    })),
    excludeTickmillProductIds: values.excludeTickmillProducts.map(({ value }) => Number(value)),
    possibleTickmillProductIds: values.possibleTickmillProducts.map(({ value }) => Number(value)),
    isSilent: values.isSilent,
    isPinned: values.isPinned,
    isClosable: values.isClosable,
    countryIds: values.countryIds || null,
    languageIds: values.languageIds || null,
    baseUrlTypeId: values.baseUrlTypeId || null,
    notificationAudienceTypes: (values.notificationAudienceTypes || []).map((audience) => ({
      id: audience.id,
      name: audience.value || audience.name,
    })),
    platformIds: values.platformIds?.map((platform) => platform.id) || [],
    images: images,
  }
}

export const NotificationDetailsForm = withFormik<OuterProps, UpdateNotificationFormValues>({
  mapPropsToValues: ({ notification, notificationTypes }) => {
    const data: UpdateNotificationFormValues = {
      id: notification?.id ?? '',
      text: notification?.text ?? '',
      name: notification?.name ?? '',
      title: notification?.title ?? '',
      shortText: notification?.shortText ?? '',
      type:
        notification?.notificationType ?? (notificationTypes.length ? notificationTypes[0] : null),
      isEnabled: notification?.isEnabled ?? true,
      ctaUrl: notification?.ctaUrl ?? '',
      ctaText: notification?.ctaText ?? '',
      tickmillCompanies: (notification?.tickmillCompanies || []).map((company) => ({
        label: company.name,
        value: company.id,
      })),
      tickmillProducts: (notification?.tickmillProducts || []).map((p) => ({
        id: p.id,
        name: p.name,
        statuses: (p.statuses || []).map((status) => ({
          id: status.id,
          name: status.name,
        })),
      })),
      excludeTickmillProducts: (notification?.excludeTickmillProducts || []).map((p) => ({
        label: p.name,
        value: p.id,
      })),
      possibleTickmillProducts: (notification?.possibleTickmillProducts || []).map((p) => ({
        label: p.name,
        value: p.id,
      })),

      startDateTime: notification?.startDateTime ?? '',
      endDateTime: notification?.endDateTime ?? '',
      isSilent: notification?.isSilent ?? false,
      isPinned: notification?.isPinned ?? false,
      isClosable: notification?.isClosable ?? true,
      baseUrlTypeId: notification?.ctaUrlType?.id ?? 0,
      countryIds: notification?.countryList ?? null,
      languageIds: notification?.notificationLanguages?.map((l) => l.id) ?? null,
      notificationAudienceTypes: (notification?.notificationAudiences || []).map((audience) => ({
        id: audience.typeId,
        name: audience.typeName,
        hasValue: !!audience.value,
        value: audience.value || '',
      })),
      platformIds:
        notification?.notificationPlatforms?.map((platform) => ({
          id: platform.id,
          name: platform.name,
        })) || [],
    }
    if (!data.id) {
      delete data.id
    }
    return data
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values?.id ?? '', dataToSubmit(values, props.notification?.images || []))
    } finally {
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
  validate: (values) => {
    const errors: FormikErrors<UpdateNotificationFormValues> = {}
    if (!values.name) {
      errors.name = 'Required'
    } else if (InvalidCharsPattern.test(values.name)) {
      errors.name = 'Characters ; % & are not allowed'
    }
    if (!values.title) errors.title = 'Required'
    if (!values.type) errors.type = 'Required'
    if (!values.startDateTime) errors.startDateTime = 'Required'
    if (!values.endDateTime) errors.endDateTime = 'Required'
    if (!values.text) errors.text = 'Required'
    if (!values.ctaUrl && values.ctaText) errors.ctaUrl = 'Required'
    if (!values.ctaText && values.ctaUrl) errors.ctaText = 'Required'

    return errors
  },
})(NotificationDetailsFormUI)
